import React, { useEffect, useRef, useState } from "react";
import api, { getExecutionContract, exportExecutionContract, unfreezeTaskProductDocument } from "./api";
import { defaultHandoffPlan, defaultHandoffPrompt } from "./handoffDocuments";
import { VIEWER_READ_ONLY_MESSAGE } from "./orgPermissions";
import "./EngineeringHandoffPanel.css";

function shortenHash(value) {
  const text = String(value || "");
  if (text.length <= 12) return text;
  return `${text.slice(0, 12)}…`;
}

export default function EngineeringHandoffPanel({
  plan,
  repo,
  mutateAllowed = true,
  onSaved,
  onUnfrozen,
}) {
  const [prompt, setPrompt] = useState(() => defaultHandoffPrompt(plan, repo));
  const [planText, setPlanText] = useState(() => defaultHandoffPlan(plan));
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [contract, setContract] = useState(null);
  const [contractError, setContractError] = useState("");
  const [contractCode, setContractCode] = useState("");
  const [contractLoading, setContractLoading] = useState(false);
  const [unfreezing, setUnfreezing] = useState(false);
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (dirtyRef.current) return;
    setPrompt(defaultHandoffPrompt(plan, repo));
    setPlanText(defaultHandoffPlan(plan));
    // Reset from persisted fields only. The full plan/repo objects change identity on poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit plan/repo object identity
  }, [
    plan?._id,
    plan?.founderHandoffPrompt,
    plan?.founderHandoffPlan,
    plan?.featureRequestText,
    plan?.engineerStartPack?.markdown,
    repo?._id,
  ]);

  useEffect(() => {
    if (!plan?._id) return undefined;
    let cancelled = false;
    setContractLoading(true);
    setContractError("");
    setContractCode("");
    getExecutionContract(plan._id)
      .then((data) => {
        if (cancelled) return;
        setContract(data?.contract || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setContract(null);
        setContractCode(err?.response?.data?.code || "");
        setContractError(err?.response?.data?.error || err.message || "Could not load Execution Contract.");
      })
      .finally(() => {
        if (!cancelled) setContractLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [plan?._id, plan?.productDocument?.fingerprint, repo?.indexedCommitSha]);

  const copy = async (value, which) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
    } catch {
      setCopied("");
    }
  };

  const copyExport = async (format, which) => {
    if (!plan?._id) return;
    try {
      const data = await exportExecutionContract(plan._id, format);
      const text = typeof data === "string" ? data : JSON.stringify(data, null, 2);
      await copy(text, which);
    } catch (err) {
      setContractCode(err?.response?.data?.code || "");
      setContractError(err?.response?.data?.error || err.message || "Could not export Execution Contract.");
    }
  };

  const save = async () => {
    if (!mutateAllowed || !plan?._id) return;
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const response = await api.put(`/feature-plans/${plan._id}`, {
        founderHandoffPrompt: prompt,
        founderHandoffPlan: planText,
      });
      dirtyRef.current = false;
      setSaved(true);
      onSaved?.(response.data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const downloadJson = async () => {
    if (!plan?._id) return;
    try {
      const data = await exportExecutionContract(plan._id, "json");
      const text = typeof data === "string" ? data : JSON.stringify(data);
      const blob = new Blob([text], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `execution-contract-${contract?.contractId || plan._id}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setContractCode(err?.response?.data?.code || "");
      setContractError(err?.response?.data?.error || err.message || "Could not download Execution Contract.");
    }
  };

  const unfreeze = async () => {
    if (!mutateAllowed || !plan?._id || unfreezing) return;
    setUnfreezing(true);
    setError("");
    try {
      const data = await unfreezeTaskProductDocument(plan._id);
      setContract(null);
      setContractError("This Task is unfrozen for editing. Approve it again to restore the Execution Contract.");
      setContractCode("NOT_FROZEN");
      onUnfrozen?.(data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not unfreeze this Task.");
    } finally {
      setUnfreezing(false);
    }
  };

  const readOnly = !mutateAllowed;
  const stale = /STALE|MISMATCH/i.test(contractCode);
  const product = contract?.approvedProduct || null;
  const guidance = contract?.engineeringGuidance || null;
  const requirements = Array.isArray(product?.requirements) ? product.requirements : [];
  const decisions = Array.isArray(product?.decisions) ? product.decisions : [];
  const exclusions = Array.isArray(product?.exclusions) ? product.exclusions : [];
  const successChecks = Array.isArray(product?.successChecks) ? product.successChecks : [];
  const risks = Array.isArray(product?.risksAndDeferrals) ? product.risksAndDeferrals : [];
  const surface = Array.isArray(guidance?.expectedModificationSurface)
    ? guidance.expectedModificationSurface
    : [];
  const steps = Array.isArray(guidance?.implementationSteps) ? guidance.implementationSteps : [];
  const criteria = Array.isArray(contract?.verificationRequirements?.criteria)
    ? contract.verificationRequirements.criteria
    : [];
  const successCount = criteria.length || successChecks.length;
  const expectedFileCount = surface.length;

  return (
    <section className="engineering-handoff" data-testid="engineering-handoff">
      <section className="execution-contract-panel" data-testid="execution-contract-panel">
        <header>
          <p className="task-story-kicker">Engineering</p>
          <h3>Approved Execution Contract</h3>
          <p>
            This is the frozen agreement Cursor, Codex, or another agent should receive. It is
            compiled from the approved Task, Intent Contract, Feature Plan, and indexed commit.
          </p>
        </header>
        {contractLoading ? (
          <p data-testid="execution-contract-loading">Loading contract…</p>
        ) : null}
        {contractError ? (
          <p
            className="engineering-handoff-error"
            role="alert"
            data-testid={stale ? "execution-contract-stale" : "execution-contract-blocked"}
          >
            {contractError}
          </p>
        ) : null}
        {contract ? (
          <>
            <dl className="execution-contract-meta">
              <div>
                <dt>Content hash</dt>
                <dd data-testid="execution-contract-hash">{shortenHash(contract.contentHash)}</dd>
              </div>
              <div>
                <dt>Indexed commit</dt>
                <dd data-testid="execution-contract-sha">{contract.repository?.indexedCommitSha}</dd>
              </div>
              <div>
                <dt>Success checks</dt>
                <dd data-testid="execution-contract-success-count">{successCount}</dd>
              </div>
              <div>
                <dt>Expected files</dt>
                <dd data-testid="execution-contract-file-count">{expectedFileCount}</dd>
              </div>
            </dl>
            <div className="execution-contract-summary" data-testid="execution-contract-summary">
              <h4>{product?.title || "Approved Task"}</h4>
              {product?.outcome ? <p>{product.outcome}</p> : null}
            </div>
            <div className="execution-contract-actions">
              <button
                type="button"
                className="engineering-secondary"
                data-testid="execution-contract-copy-markdown"
                onClick={() => copyExport("markdown", "markdown")}
              >
                {copied === "markdown" ? "Copied" : "Copy agent prompt"}
              </button>
              <button
                type="button"
                className="engineering-secondary"
                data-testid="execution-contract-download"
                onClick={downloadJson}
              >
                Download JSON
              </button>
              {mutateAllowed ? (
                <button
                  type="button"
                  className="engineering-secondary"
                  data-testid="execution-contract-unfreeze"
                  onClick={() => void unfreeze()}
                  disabled={unfreezing}
                >
                  {unfreezing ? "Unfreezing…" : "Unfreeze to edit"}
                </button>
              ) : null}
            </div>
            <div className="execution-contract-details" data-testid="execution-contract-details">
              {requirements.length ? (
                <section data-testid="execution-contract-requirements">
                  <h5>Requirements</h5>
                  <ol>
                    {requirements.map((item) => (
                      <li key={item.id || item.title}>
                        <strong>{item.title}</strong>
                        {item.explanation ? <p>{item.explanation}</p> : null}
                      </li>
                    ))}
                  </ol>
                </section>
              ) : null}
              {decisions.length ? (
                <section data-testid="execution-contract-decisions">
                  <h5>Product decisions</h5>
                  <ul>
                    {decisions.map((item) => (
                      <li key={item.id || item.selected}>
                        <strong>{item.selected}</strong>
                        {item.consequence ? <p>{item.consequence}</p> : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              {exclusions.length ? (
                <section data-testid="execution-contract-exclusions">
                  <h5>Out of scope</h5>
                  <ul>
                    {exclusions.map((item) => (
                      <li key={item.id || item.text}>{item.text}</li>
                    ))}
                  </ul>
                </section>
              ) : null}
              {(successChecks.length || criteria.length) ? (
                <section data-testid="execution-contract-success-checks">
                  <h5>Success checks</h5>
                  <ul>
                    {(successChecks.length ? successChecks : criteria).map((item) => (
                      <li key={item.id || item.requirementId || item.text}>{item.text}</li>
                    ))}
                  </ul>
                </section>
              ) : null}
              {risks.length ? (
                <section data-testid="execution-contract-risks">
                  <h5>Risks and deferrals</h5>
                  <ul>
                    {risks.map((item) => (
                      <li key={item.id || item.what}>
                        <strong>{item.what}</strong>
                        {item.why ? <p>{item.why}</p> : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              {surface.length ? (
                <section data-testid="execution-contract-files">
                  <h5>Expected files</h5>
                  <ul>
                    {surface.map((item) => (
                      <li key={`${item.filePath}:${item.symbolName || ""}`}>
                        <code>{item.filePath}</code>
                        {item.classification ? (
                          <span className="execution-contract-file-class"> · {item.classification}</span>
                        ) : null}
                        {item.rationale ? <p>{item.rationale}</p> : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              {steps.length ? (
                <section data-testid="execution-contract-steps">
                  <h5>Implementation steps</h5>
                  <ol>
                    {steps.map((item) => (
                      <li key={item.stepNumber || item.instruction}>{item.instruction}</li>
                    ))}
                  </ol>
                </section>
              ) : null}
            </div>
          </>
        ) : null}
      </section>

      <header>
        <h3>Working notes</h3>
        <p data-testid="engineering-notes-disclaimer">
          These editable prompt and plan fields are supplemental working notes. They do not modify
          the approved Execution Contract.
        </p>
        {readOnly ? <p className="engineering-handoff-readonly">{VIEWER_READ_ONLY_MESSAGE}</p> : null}
      </header>

      <div className="engineering-handoff-grid">
        <div className="engineering-handoff-doc">
          <div className="engineering-handoff-doc-head">
            <label htmlFor="engineering-prompt">Working prompt</label>
            <button
              type="button"
              className="engineering-secondary"
              onClick={() => copy(prompt, "prompt")}
            >
              {copied === "prompt" ? "Copied" : "Copy prompt"}
            </button>
          </div>
          <textarea
            id="engineering-prompt"
            data-testid="engineering-prompt"
            value={prompt}
            onChange={(event) => {
              dirtyRef.current = true;
              setSaved(false);
              setPrompt(event.target.value);
            }}
            rows={16}
            readOnly={readOnly}
          />
        </div>
        <div className="engineering-handoff-doc">
          <div className="engineering-handoff-doc-head">
            <label htmlFor="engineering-plan">Working plan</label>
            <button
              type="button"
              className="engineering-secondary"
              onClick={() => copy(planText, "plan")}
            >
              {copied === "plan" ? "Copied" : "Copy plan"}
            </button>
          </div>
          <textarea
            id="engineering-plan"
            data-testid="engineering-plan"
            value={planText}
            onChange={(event) => {
              dirtyRef.current = true;
              setSaved(false);
              setPlanText(event.target.value);
            }}
            rows={16}
            readOnly={readOnly}
          />
        </div>
      </div>

      <div className="engineering-handoff-actions">
        <button
          type="button"
          className="engineering-secondary"
          onClick={() => copy(`${prompt}\n\n---\n\n${planText}`, "both")}
        >
          {copied === "both" ? "Copied" : "Copy both notes"}
        </button>
        {mutateAllowed ? (
          <button
            type="button"
            className="founder-primary"
            data-testid="engineering-save"
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving…" : saved ? "Saved" : "Save notes"}
          </button>
        ) : null}
      </div>
      {error ? (
        <p className="engineering-handoff-error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}
